import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoreService from '../../services/storeService';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ConfirmDialog from '../common/ConfirmDialog';
import FullImageDialog from '../common/FullImageDialog';
import { fieldLabelSx, buildTextFieldSx } from '../common/formFieldStyles';
import ImagePickerField from '../common/ImagePickerField';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SortableStoreGalleryGrid from './SortableStoreGalleryGrid';
import { useToast } from '../../context/ToastContext';
import { PWA_APP_NAME_MAX_LENGTH } from '../../constants/pwaBranding';
import { validateImageFiles, MAX_IMAGE_SIZE_MB, ALLOWED_IMAGE_EXTENSIONS } from '../../utils/imageValidation';
import { resizeImagesForUpload } from '../../utils/imageResize';
import { galleryImagesFromStore } from '../../utils/storeGalleryImages';
import {
  STORE_IMAGE_CATEGORY_GALLERY,
  STORE_IMAGE_CATEGORY_LOGO,
  isStoreLogoImage,
} from '../../constants/storeImageCategories';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const MAX_IMAGES = 8;
const IMAGE_INPUT_ID = 'store-images-upload-input';

const logoImageFromStore = (store) => (store?.images || []).find(isStoreLogoImage) || null;

const StoreImagesPanel = ({ storeId, store, onStoreChanged }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const [images, setImages] = useState(() => galleryImagesFromStore(store));
  const [logoImage, setLogoImage] = useState(() => logoImageFromStore(store));
  const [appName, setAppName] = useState(() => store?.pwaAppName || '');
  const [appNameErrorKey, setAppNameErrorKey] = useState(null);
  const [savingAppName, setSavingAppName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [deleting, setDeleting] = useState(new Set());
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, imageId: null, imageUrl: null });
  const [previewUrl, setPreviewUrl] = useState(null);

  const canAdd = hasPermission(Permissions.StoresImagesAdd);
  const canDelete = hasPermission(Permissions.StoresImagesDelete);
  const canUpdate = hasPermission(Permissions.StoresUpdate);
  const canReorder = canAdd && images.length > 1;
  const savedAppName = store?.pwaAppName || '';
  const appNameChanged = appName.trim() !== savedAppName.trim();

  useEffect(() => {
    const nextGallery = galleryImagesFromStore(store);
    setImages((current) => {
      const sameImages = current.length === nextGallery.length
        && current.every((image) => nextGallery.some((next) => next.id === image.id));
      return sameImages ? current : nextGallery;
    });
    setLogoImage(logoImageFromStore(store));
    setAppName(store?.pwaAppName || '');
    setAppNameErrorKey(null);
  }, [store]);

  const resetFileInput = () => {
    const fileInput = document.getElementById(IMAGE_INPUT_ID);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const refreshImages = useCallback(async () => {
    if (onStoreChanged) {
      await onStoreChanged();
      return;
    }
    const nextStore = await StoreService.getStore(storeId);
    setImages(galleryImagesFromStore(nextStore));
    setLogoImage(logoImageFromStore(nextStore));
    setAppName(nextStore?.pwaAppName || '');
  }, [onStoreChanged, storeId]);

  const handleSaveAppName = async () => {
    if (!canUpdate) return;
    const nextName = appName.trim();
    if (nextName.length > PWA_APP_NAME_MAX_LENGTH) {
      setAppNameErrorKey('stores.images.appNameTooLong');
      return;
    }

    try {
      setSavingAppName(true);
      setError(null);
      setAppNameErrorKey(null);
      await StoreService.updatePwaAppName(storeId, nextName);
      setAppName(nextName);
      showSuccess(t('stores.images.appNameSaved'));
      await refreshImages();
    } catch (err) {
      console.error('Error saving app name:', err);
      setError(err.response?.data?.message || t('stores.images.error.savingAppName'));
    } finally {
      setSavingAppName(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const totalAfterUpload = images.length + files.length;
    if (totalAfterUpload > MAX_IMAGES) {
      const allowedCount = MAX_IMAGES - images.length;
      setError(t('stores.images.error.maxImages', {
        max: MAX_IMAGES,
        current: images.length,
        allowed: allowedCount,
      }));
      resetFileInput();
      return;
    }

    const validation = validateImageFiles(files, t);
    if (!validation.valid) {
      setError(validation.errors.join('\n'));
      resetFileInput();
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const resizedFiles = await resizeImagesForUpload(files);
      await StoreService.addImages(storeId, resizedFiles.map((file) => ({ file })), STORE_IMAGE_CATEGORY_GALLERY);
      resetFileInput();
      await refreshImages();
    } catch (err) {
      console.error('Error uploading images:', err);
      setError(err.response?.data?.message || t('stores.images.error.uploading'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const { imageId } = confirmDialog;
    if (!imageId) return;

    try {
      setDeleting((prev) => new Set(prev).add(imageId));
      setError(null);
      await StoreService.deleteImage(storeId, imageId);
      setConfirmDialog({ open: false, imageId: null, imageUrl: null });
      await refreshImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.response?.data?.message || t('stores.images.error.deleting'));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }
  };

  const persistReorder = async (nextImages) => {
    const previousImages = images;
    setImages(nextImages);

    try {
      setReordering(true);
      setError(null);
      await StoreService.reorderImages(
        storeId,
        nextImages.map((image) => image.id),
      );
    } catch (err) {
      console.error('Error reordering images:', err);
      setImages(previousImages);
      showError(t('stores.images.error.reorder'));
    } finally {
      setReordering(false);
    }
  };

  const handleLogoSelect = async (file) => {
    if (!canAdd || !file) return;
    try {
      setLogoUploading(true);
      setError(null);
      await StoreService.addImages(storeId, [{ file }], STORE_IMAGE_CATEGORY_LOGO);
      await refreshImages();
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.message || t('stores.images.error.uploading'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!canDelete || !logoImage?.id) return;
    try {
      setLogoUploading(true);
      setError(null);
      await StoreService.deleteImage(storeId, logoImage.id);
      await refreshImages();
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err.response?.data?.message || t('stores.images.error.deleting'));
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(canAdd || canDelete || logoImage || canUpdate) && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3} alignItems="flex-start">
            {(canAdd || canDelete || logoImage) && (
              <Grid item xs={12} md="auto">
                <ImagePickerField
                  label={t('stores.images.logoTitle')}
                  helpText={t('stores.images.logoHelp')}
                  imageUrl={logoImage?.url}
                  onSelect={handleLogoSelect}
                  onRemove={handleLogoRemove}
                  disabled={!canAdd && !canDelete}
                  processing={logoUploading}
                  removeConfirmMessage={t('stores.images.confirmDeleteLogo')}
                  cropToSquare
                />
              </Grid>
            )}
            {canUpdate && (
              <Grid item xs={12} md>
                <Typography variant="subtitle2" sx={fieldLabelSx}>
                  {t('stores.images.appNameTitle')}
                </Typography>
                <TextField
                  fullWidth
                  value={appName}
                  onChange={(event) => {
                    setAppName(event.target.value);
                    setAppNameErrorKey(null);
                  }}
                  placeholder={t('stores.images.appNamePlaceholder')}
                  disabled={savingAppName}
                  error={Boolean(appNameErrorKey)}
                  helperText={
                    appNameErrorKey
                      ? t(appNameErrorKey, { max: PWA_APP_NAME_MAX_LENGTH })
                      : `${appName.trim().length}/${PWA_APP_NAME_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: PWA_APP_NAME_MAX_LENGTH }}
                  sx={{ ...buildTextFieldSx(Boolean(appNameErrorKey)), maxWidth: 360 }}
                />
                <Box sx={{ mt: 2 }}>
                  <PrimaryActionButton
                    onClick={handleSaveAppName}
                    disabled={savingAppName || !appNameChanged}
                  >
                    {savingAppName ? t('common.saving') : t('stores.images.saveAppName')}
                  </PrimaryActionButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 2 }}>
        {t('stores.images.galleryTitle')}
      </Typography>
      {canAdd && images.length > 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('stores.images.reorderHelp')}
        </Typography>
      )}

      {canAdd && (
        <Box sx={{ mb: 3 }}>
          <input
            id={IMAGE_INPUT_ID}
            type="file"
            accept={ALLOWED_IMAGE_EXTENSIONS.join(',')}
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            component="label"
            htmlFor={IMAGE_INPUT_ID}
            disabled={uploading || images.length >= MAX_IMAGES}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            sx={{
              textTransform: 'none',
              borderColor: BRAND.accent,
              color: BRAND.accentText,
              '&:hover': {
                borderColor: BRAND.accent,
                backgroundColor: brandAlpha('accent', 0.1),
              },
            }}
          >
            {uploading ? t('stores.images.uploading') : t('stores.images.addImages')}
          </Button>
          {images.length >= MAX_IMAGES && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('stores.images.maxImagesReached', { max: MAX_IMAGES })}
            </Typography>
          )}
        </Box>
      )}

      {images.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t('stores.images.noImages')}
          </Typography>
        </Box>
      ) : (
        <SortableStoreGalleryGrid
          images={images}
          canReorder={canReorder}
          canDelete={canDelete}
          deletingIds={deleting}
          disabled={reordering}
          onReorder={persistReorder}
          onPreview={setPreviewUrl}
          onDelete={(image) => setConfirmDialog({ open: true, imageId: image.id, imageUrl: image.url })}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, imageId: null, imageUrl: null })}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirm')}
        message={t('stores.images.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        imageUrl={confirmDialog.imageUrl}
      />
      <FullImageDialog
        open={Boolean(previewUrl)}
        imageUrl={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </Box>
  );
};

export default StoreImagesPanel;
